import * as Sentry from "@sentry/react";
import { ApiRequestError, NetworkRequestError } from "../../../lib/api";
import { decideApiErrorCapture } from "../../../lib/api-error-capture";
import type { ScheduleOperation } from "../../../lib/rd-schedule-actions";

// NO-876. The report for dialog requests that fail in the browser. A 5xx is
// reported from both sides; this also covers what only the client sees: a
// network failure, a response it could not read. A 4xx is the API refusing a
// change (a taken slot, a visit that moved), which the dialog already shows,
// so it is not a defect. Tags only, never the error message, a path, an id or
// any value the RD typed.

function failureCode(
  error: unknown
): "network_error" | "http_error" | "unexpected_response" {
  if (error instanceof NetworkRequestError) return "network_error";

  return error instanceof ApiRequestError ? "http_error" : "unexpected_response";
}

/**
 * The shared scrubber keeps only operation values it knows, so reads, the
 * submit and the retry map onto the ones it already allows.
 */
function safeOperation(operation: ScheduleOperation): "read" | "save" | "retry" {
  if (operation === "retry") return "retry";

  return operation === "submit" ? "save" : "read";
}

export function captureScheduleFailure(
  operation: ScheduleOperation,
  error: unknown
): void {
  const decision = decideApiErrorCapture(error);

  if (!decision.capture) return;

  if (error instanceof ApiRequestError && error.status < 500) return;

  const code = failureCode(error);

  Sentry.captureException(new Error("rd_schedule_action_client_failed"), {
    level: decision.level,
    tags: {
      surface: "rd_schedule_actions",
      source: "client",
      operation: safeOperation(operation),
      ...(error instanceof ApiRequestError ? { status: String(error.status) } : {}),
      code,
    },
  });
}

/** A wait that ran out: the change is saved but Healthie has not settled it. */
export function captureScheduleWaitTimeout(): void {
  Sentry.captureException(new Error("rd_schedule_action_client_failed"), {
    level: "warning",
    tags: {
      surface: "rd_schedule_actions",
      source: "client",
      operation: "read",
      code: "timeout",
    },
  });
}
